import { useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import { FEATURE_FLAG_SUPPORT_AI_BOT } from '../../constants/featureFlags';

const FLAG_LOAD_TIMEOUT_MS = 2_000;

export type SupportAiBotFeatureFlagState = {
  enabled: boolean;
  isLoading: boolean;
};

const isPostHogConfigured = Boolean(
  (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_PUBLIC_POSTHOG_KEY &&
    (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_PUBLIC_POSTHOG_HOST,
);

/**
 * Fail-closed release gate for every support UI entry point. It intentionally
 * does not depend on the psychologist Redux state: meeting clients receive a
 * separate PostHog identity and must be evaluated by the same flag.
 */
export function useSupportAiBotFeatureFlag(configured = isPostHogConfigured): SupportAiBotFeatureFlagState {
  const posthog = usePostHog();
  const [state, setState] = useState<SupportAiBotFeatureFlagState>(() =>
    resolveSupportAiBotFeatureFlagState(posthog, configured),
  );

  useEffect(() => {
    if (!configured) {
      setStateIfChanged({ enabled: false, isLoading: false });
      return;
    }

    const update = () => setStateIfChanged(resolveSupportAiBotFeatureFlagState(posthog, configured));
    const unsubscribe = posthog.onFeatureFlags(update);
    update();

    const timeout = window.setTimeout(() => {
      setState((current) => (current.isLoading ? { enabled: false, isLoading: false } : current));
    }, FLAG_LOAD_TIMEOUT_MS);

    return () => {
      unsubscribe?.();
      window.clearTimeout(timeout);
    };
  }, [configured, posthog]);

  return state;

  function setStateIfChanged(next: SupportAiBotFeatureFlagState) {
    setState((current) => (current.enabled === next.enabled && current.isLoading === next.isLoading ? current : next));
  }
}

export function resolveSupportAiBotFeatureFlagState(
  posthog: ReturnType<typeof usePostHog>,
  configured = isPostHogConfigured,
): SupportAiBotFeatureFlagState {
  if (!configured || !posthog.featureFlags?.hasLoadedFlags) {
    return { enabled: false, isLoading: configured };
  }

  return {
    enabled: posthog.isFeatureEnabled(FEATURE_FLAG_SUPPORT_AI_BOT) === true,
    isLoading: false,
  };
}
